import type {
  CartGuestSessionItemApi,
  CartItem,
  CartMutationResponseApi,
  CartPointsUsageResponseApi,
  CartResponseApi,
  CartVariantImage,
  NormalizedCart,
} from "./types";
import {
  getGroupedVariantParent,
  getGroupedVariantParentsHeader,
  GROUPED_VARIANT_PARENTS_HEADER,
} from "./groupedVariantParents";

const PRODUCT_FALLBACK_IMAGE = "/Office%20Furniture/image-removebg-preview.png";

function slugifyProductNameBase(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

function getNonEmptyString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function slugifyProductName(name: string, fallbackId: number) {
  const slug = slugifyProductNameBase(name);

  if (!slug) {
    return `product-${fallbackId}`;
  }

  return slug.endsWith(`-${fallbackId}`) ? slug : `${slug}-${fallbackId}`;
}

function parsePrice(value: string | number | null | undefined) {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return 0;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function parseNullableNumber(value: string | number | null | undefined) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function normalizeRatingValue(value?: string | null) {
  if (!value) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : Math.max(0, Math.min(parsed, 5));
}

function buildMediaUrl(path: string) {
  if (!path) {
    return PRODUCT_FALLBACK_IMAGE;
  }

  if (path.startsWith("http://") || path.startsWith("https://")) {
    const url = new URL(path);
    url.pathname = url.pathname.replace(/\/{2,}/g, "/");
    return url.toString();
  }

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;

  if (!apiBaseUrl) {
    return path;
  }

  const mediaBaseUrl = apiBaseUrl.endsWith("/api")
    ? apiBaseUrl.slice(0, -4)
    : apiBaseUrl.replace(/\/api\/?$/, "");

  const normalizedPath = `/${path}`.replace(/\/{2,}/g, "/");
  return `${mediaBaseUrl}${normalizedPath}`;
}

function getSortedImages(images: CartVariantImage[]) {
  return [...images].sort((firstImage, secondImage) => {
    if (firstImage.order !== secondImage.order) {
      return firstImage.order - secondImage.order;
    }

    if (firstImage.is_primary === secondImage.is_primary) {
      return firstImage.id - secondImage.id;
    }

    return firstImage.is_primary ? -1 : 1;
  });
}

function normalizeCartItem(
  item: {
    id?: number;
    item_id?: number;
    variant_id?: number;
    product_variant_id?: number;
    sku?: string;
    price?: string | number;
    quantity?: number;
    subtotal?: string | number;
    subtotal_before_discount?: number;
    subtotal_after_discount?: number;
    variant?: CartGuestSessionItemApi["variant"];
    product_variant?: CartGuestSessionItemApi["product_variant"];
  },
  index: number,
): CartItem | null {
  const variant = item.variant ?? item.product_variant;

  if (!variant) {
    return null;
  }

  const variantId =
    (Number.isInteger(variant.id) && (variant.id as number) > 0
      ? variant.id
      : null) ??
    (Number.isInteger(item.variant_id) && (item.variant_id as number) > 0
      ? item.variant_id
      : null) ??
    (Number.isInteger(item.product_variant_id) &&
    (item.product_variant_id as number) > 0
      ? item.product_variant_id
      : null);

  if (!variantId) {
    return null;
  }

  const sortedImages = getSortedImages(variant.images ?? []);
  const primaryImage =
    sortedImages.find((image) => image.is_primary) ?? sortedImages[0] ?? null;
  const hoverImage =
    sortedImages.find((image) => image.order === 2) ??
    sortedImages[1] ??
    primaryImage;

  const gallery =
    sortedImages.length > 0
      ? sortedImages.map((image) => buildMediaUrl(image.file))
      : [PRODUCT_FALLBACK_IMAGE];

  const sku = getNonEmptyString(variant.sku) ?? "";
  const name =
    getNonEmptyString(variant.name) ??
    getNonEmptyString(variant.product_name) ??
    "";

  const quantity = item.quantity ?? 0;
  const price = parsePrice(variant.price);
  const discountPrice = parsePrice(variant.discount_price);
  const displayDiscountPrice = price;

  return {
    itemId: item.id ?? item.item_id ?? index + 1,
    variantId,
    parentVariantId:
      parseNullableNumber(variant.parent_variant_id) ??
      getGroupedVariantParent(variantId) ??
      variantId,
    slug: slugifyProductName(name, variantId),
    sku,
    quantity,
    price,
    discountPrice,
    displayDiscountPrice,
    subtotalBeforeDiscount: parsePrice(item.subtotal_before_discount),
    subtotalAfterDiscount: parsePrice(item.subtotal_after_discount),
    stock: variant.stock ?? 0,
    isPublic: variant.is_public ?? false,
    isNew: variant.is_new ?? false,
    rating: normalizeRatingValue(variant.rating),
    image: primaryImage
      ? buildMediaUrl(primaryImage.file)
      : PRODUCT_FALLBACK_IMAGE,
    hoverImage: hoverImage
      ? buildMediaUrl(hoverImage.file)
      : PRODUCT_FALLBACK_IMAGE,
    gallery,
    optionValues:
      variant.option_values ??
      variant.grouped_option_values?.flatMap((group) => group.options) ??
      [],
    name,
  };
}

export function normalizeCartResponse(data: CartResponseApi): NormalizedCart {
  const items = data.results
    .map((item, index) => normalizeCartItem(item, index))
    .filter((item): item is CartItem => item !== null);
  return {
    type: data.type,
    items,
    total: parsePrice(data.total),
    totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
    count: data.count,
    remainingDays: data.remaining_days ?? null,
    currentDiscountPercentage: parseNullableNumber(data.discount_percentage),
    remainingDiscountPercentage: null,
    totalAfterMaxDiscount: parseNullableNumber(data.total_after_max_discount),
    message: data.type === "guest" ? (data.message ?? null) : null,
  };
}

function buildAuthHeaders(accessToken?: string | null) {
  const headers: Record<string, string> = {};

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

async function parseJsonResponse<T>(response: Response) {
  const text = await response.text();

  if (!text) {
    return null as T | null;
  }

  return JSON.parse(text) as T;
}

function getApiErrorMessage(
  data:
    | CartResponseApi
    | CartMutationResponseApi
    | CartPointsUsageResponseApi
    | { error?: string; detail?: string; message?: string }
    | null,
) {
  if (!data || typeof data !== "object") {
    return null;
  }

  if ("detail" in data && typeof data.detail === "string") {
    return data.detail;
  }

  if ("error" in data && typeof data.error === "string") {
    return data.error;
  }

  if ("message" in data && typeof data.message === "string") {
    return data.message;
  }

  return null;
}

export async function getCart(accessToken?: string | null) {
  const groupedVariantParents = getGroupedVariantParentsHeader();
  const response = await fetch("/api/cart", {
    method: "GET",
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(groupedVariantParents
        ? { [GROUPED_VARIANT_PARENTS_HEADER]: groupedVariantParents }
        : {}),
      ...buildAuthHeaders(accessToken),
    },
  });

  const data = await parseJsonResponse<
    CartResponseApi | { error?: string; detail?: string }
  >(response);

  if (!response.ok || !data || !("type" in data)) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to load cart");
  }

  return normalizeCartResponse(data);
}

export async function addCartItem(
  variantId: number,
  accessToken?: string | null,
) {
  const response = await fetch("/api/cart/add", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
    body: JSON.stringify({ variant_id: variantId }),
  });

  const data = await parseJsonResponse<
    | CartMutationResponseApi
    | { detail?: string; message?: string; error?: string }
  >(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to add item to cart");
  }

  return data as CartMutationResponseApi;
}

export async function updateCartItemQuantity(
  itemId: number,
  quantity: number,
  accessToken?: string | null,
) {
  const response = await fetch("/api/cart/update", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
    body: JSON.stringify({ item_id: itemId, quantity }),
  });

  const data = await parseJsonResponse<
    | CartMutationResponseApi
    | { detail?: string; message?: string; error?: string }
  >(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to update cart item");
  }

  return data as CartMutationResponseApi;
}

export async function removeCartItem(
  itemId: number,
  accessToken?: string | null,
) {
  const response = await fetch("/api/cart/remove", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
    body: JSON.stringify({ item_id: itemId }),
  });

  const data = await parseJsonResponse<
    | CartMutationResponseApi
    | { detail?: string; message?: string; error?: string }
  >(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to remove cart item");
  }

  return data as CartMutationResponseApi;
}

export async function applyCartPoints(
  points: number,
  accessToken?: string | null,
) {
  const response = await fetch("/api/cart/use-points", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
    body: JSON.stringify({ points }),
  });

  const data = await parseJsonResponse<
    | CartPointsUsageResponseApi
    | { detail?: string; message?: string; error?: string }
  >(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to apply cart points");
  }

  return data as CartPointsUsageResponseApi;
}
